(() => {
  const BONUS_ITEM_PROPERTY = '_bonus_item';
  const BONUS_VARIANT_PROPERTY = '_bonus_variant_id';
  let queue = Promise.resolve();

  const enqueue = (task) => {
    const next = queue.then(task, task);
    queue = next.catch(() => {});
    return next;
  };

  const requestJson = async (url, body) => {
    const response = await fetch(
      url,
      body
        ? { ...fetchConfig(), body: JSON.stringify(body) }
        : { headers: { Accept: 'application/json' } }
    );
    const result = await response.json();
    if (!response.ok || result.status || result.errors) {
      throw new Error(result.description || result.message || result.errors || theme.cartStrings.error);
    }
    return result;
  };

  const getCart = () => requestJson(`${theme.routes.cart_url}.js`);
  const isBonusItem = (item) => String(item.properties?.[BONUS_ITEM_PROPERTY]) === 'true';
  const getBonusVariantId = (item) => Number(item.properties?.[BONUS_VARIANT_PROPERTY]) || null;

  const findReconciliationAction = (cart) => {
    const parentsByBonus = new Map();
    const bonusesByVariant = new Map();

    cart.items.forEach((item, index) => {
      if (isBonusItem(item)) {
        const items = bonusesByVariant.get(Number(item.variant_id)) || [];
        items.push({ item, line: index + 1 });
        bonusesByVariant.set(Number(item.variant_id), items);
        return;
      }

      const bonusVariantId = getBonusVariantId(item);
      if (bonusVariantId && !item.selling_plan_allocation) {
        const items = parentsByBonus.get(bonusVariantId) || [];
        items.push(item);
        parentsByBonus.set(bonusVariantId, items);
      }
    });

    const variantIds = new Set([...parentsByBonus.keys(), ...bonusesByVariant.keys()]);
    for (const bonusVariantId of variantIds) {
      const parents = parentsByBonus.get(bonusVariantId) || [];
      const bonuses = bonusesByVariant.get(bonusVariantId) || [];

      if (parents.length === 0 && bonuses.length > 0) {
        return { type: 'remove', line: bonuses[bonuses.length - 1].line };
      }
      if (bonuses.length > 1) {
        return { type: 'remove', line: bonuses[bonuses.length - 1].line };
      }
      if (parents.length > 0 && bonuses.length === 0) {
        return { type: 'add', bonusVariantId, parentKey: parents[0].key };
      }

      if (bonuses.length === 1) {
        const bonus = bonuses[0];
        const eligibleParentKeys = new Set(parents.map((parent) => parent.key));
        const parentKey = bonus.item.parent_relationship?.parent_key;
        if (!eligibleParentKeys.has(parentKey)) {
          return { type: 'remove', line: bonus.line };
        }
        if (bonus.item.quantity !== 1) {
          return { type: 'quantity', line: bonus.line, quantity: 1 };
        }
      }
    }

    return null;
  };

  const reconcileInternal = async () => {
    let changed = false;

    for (let attempt = 0; attempt < 20; attempt += 1) {
      const cart = await getCart();
      const action = findReconciliationAction(cart);
      if (!action) return { cart, changed };

      changed = true;
      if (action.type === 'add') {
        await requestJson(`${theme.routes.cart_add_url}.js`, {
          items: [{
            id: action.bonusVariantId,
            quantity: 1,
            parent_line_key: action.parentKey,
            properties: { [BONUS_ITEM_PROPERTY]: 'true' },
          }],
        });
      } else {
        await requestJson(theme.routes.cart_change_url, {
          line: action.line,
          quantity: action.type === 'quantity' ? action.quantity : 0,
        });
      }
    }

    throw new Error('特典商品のカート整合処理が完了しませんでした。');
  };

  const getSectionTargets = () => {
    const targets = [];
    const miniCart = document.querySelector('mini-cart');
    if (miniCart) {
      miniCart.getSectionsToRender().forEach((target) => {
        const element = document.getElementById(target.id);
        if (target.section && element) targets.push({ ...target, element });
      });
    }

    // ミニカート内にも id="main-cart-items" があるため、document.getElementById ではなく
    // カートページの cart-items を起点にして対象セクションを特定する。
    const pageCart = document.querySelector('cart-items.page-width');
    const mainCartItems = pageCart?.querySelector('#main-cart-items');
    if (mainCartItems?.dataset.id) {
      targets.push({
        id: 'main-cart-items',
        section: mainCartItems.dataset.id,
        selector: '.js-contents',
        element: mainCartItems,
      });
    }

    const cartFooter = document.getElementById('main-cart-footer');
    if (cartFooter?.dataset.id) {
      targets.push({
        id: 'main-cart-footer',
        section: cartFooter.dataset.id,
        selector: '.js-contents',
        element: cartFooter,
      });
    }

    return targets;
  };

  const refreshSections = async (targets) => {
    if (targets.length === 0) return null;
    return requestJson(theme.routes.cart_update_url, {
      sections: targets.map((target) => target.section),
      sections_url: window.location.pathname,
    });
  };

  const renderSections = (response, targets) => {
    if (!response?.sections) return;

    const discountBefore = document.querySelector('discount-code')?.dataset.allDiscountCodes;
    targets.forEach((target) => {
      const element = target.element || document.getElementById(target.id);
      const html = response.sections[target.section];
      if (!element || !html) return;

      const replacement = element.querySelector(target.selector) || element;
      const content = new DOMParser().parseFromString(html, 'text/html').querySelector(target.selector);
      if (replacement && content) replacement.innerHTML = content.innerHTML;
    });

    const discountAfter = document.querySelector('discount-code');
    if (discountAfter && discountBefore) discountAfter.dataset.allDiscountCodes = discountBefore;

    if (typeof response.item_count === 'number') {
      const isEmpty = response.item_count === 0;
      document.querySelector('mini-cart')?.classList.toggle('is-empty', isEmpty);
      document.querySelector('cart-items.page-width')?.classList.toggle('is-empty', isEmpty);
      document.getElementById('main-cart-footer')?.classList.toggle('is-empty', isEmpty);
    }
  };

  const addParentsInternal = async (parentEntries, options = {}) => {
    const entries = parentEntries
      .map(({ item, bonusVariantId }) => ({ item, bonusVariantId: Number(bonusVariantId) || null }))
      .filter(({ item }) => Number(item?.id));
    if (entries.length === 0) throw new Error(theme.cartStrings.error);

    const bonusVariantIds = new Set(entries.map(({ bonusVariantId }) => bonusVariantId).filter(Boolean));
    const existingBonusVariantIds = new Set();
    if (bonusVariantIds.size > 0) {
      const cart = await getCart();
      cart.items.forEach((item) => {
        if (isBonusItem(item)) existingBonusVariantIds.add(Number(item.variant_id));
      });
    }

    const items = [];
    entries.forEach(({ item, bonusVariantId }) => {
      const parent = bonusVariantId
        ? {
            ...item,
            properties: {
              ...(item.properties || {}),
              [BONUS_VARIANT_PROPERTY]: String(bonusVariantId),
            },
          }
        : item;
      items.push(parent);

      if (bonusVariantId && !existingBonusVariantIds.has(bonusVariantId)) {
        items.push({
          id: bonusVariantId,
          quantity: 1,
          parent_id: Number(parent.id),
          properties: { [BONUS_ITEM_PROPERTY]: 'true' },
        });
        existingBonusVariantIds.add(bonusVariantId);
      }
    });

    const added = await requestJson(`${theme.routes.cart_add_url}.js`, {
      items,
      ...(options.sections?.length ? { sections: options.sections } : {}),
      ...(options.sectionsUrl ? { sections_url: options.sectionsUrl } : {}),
    });
    const reconciliation = bonusVariantIds.size > 0
      ? await reconcileInternal()
      : { changed: false };
    if (reconciliation.changed && options.sections?.length) {
      const refreshed = await requestJson(theme.routes.cart_update_url, {
        sections: options.sections,
        sections_url: options.sectionsUrl || window.location.pathname,
      });
      return { ...added, ...refreshed, id: added.id };
    }
    return added;
  };

  const addParents = (parentEntries, options) =>
    enqueue(() => addParentsInternal(parentEntries, options));

  const addParent = (parentItem, bonusVariantId, options) =>
    addParents([{ item: parentItem, bonusVariantId }], options);

  const reconcile = (options = {}) => enqueue(async () => {
    const result = await reconcileInternal();
    if (!result.changed && !options.refresh) return result;

    const targets = options.render ? getSectionTargets() : [];
    const response = await refreshSections(targets);
    if (response) renderSections(response, targets);
    return { ...result, response };
  });

  const reconcileAndNotify = (options = {}) => {
    reconcile({ render: true, ...options })
      .then((result) => {
        if (result.changed) {
          document.dispatchEvent(new CustomEvent('bonus-cart:updated', { detail: { cart: result.cart } }));
        }
      })
      .catch((error) => {
        console.error('[BonusCart] 整合処理に失敗しました:', error);
        const cartErrors = document.getElementById('cart-errors');
        if (cartErrors) {
          cartErrors.textContent = error.message || theme.cartStrings.error;
        } else {
          window.alert(error.message || theme.cartStrings.error);
        }
      });
  };

  document.addEventListener('cart:updated', reconcileAndNotify);

  let miniCartReconciled = false;
  document.addEventListener('cartdrawer:opened', () => {
    if (miniCartReconciled) return;
    miniCartReconciled = true;
    reconcileAndNotify({ refresh: true });
  });

  const reconcileInitialCartPage = () => {
    if (document.querySelector('cart-items.page-width')) reconcileAndNotify();
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', reconcileInitialCartPage, { once: true });
  } else {
    reconcileInitialCartPage();
  }

  window.BonusCart = { addParent, addParents, reconcile };
})();
